import React from "react";

interface WatchlistToggleProps {
  isWatched: boolean;
  onToggle: () => void;
  onRequireAuth?: () => void;
  onRequirePro?: () => void;
  authed: boolean;
  proRequired: boolean;
  loading?: boolean;
  /**
   * "star" — bare ★/☆ glyph button (legacy; detail view, compact spots).
   * "pill" — labeled "Watch"/"Watching" pill with a star icon (bordered card).
   * "icon" — SVG star icon only, filled-blue when watched (dense list row).
   */
  variant?: "star" | "pill" | "icon";
}

// Five-point star path shared by the pill variant (filled when watched).
const STAR_PATH = "M9 1.8l2.1 4.3 4.7.7-3.4 3.3.8 4.7L9 12.5l-4.2 2.2.8-4.7L2.2 6.8l4.7-.7z";

const WatchlistToggle: React.FC<WatchlistToggleProps> = ({
  isWatched,
  onToggle,
  onRequireAuth,
  onRequirePro,
  authed,
  proRequired,
  loading = false,
  variant = "star",
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;
    if (!authed) {
      onRequireAuth?.();
      return;
    }
    if (proRequired) {
      onRequirePro?.();
      return;
    }
    onToggle();
  };

  const ariaLabel = isWatched ? "Remove from watchlist" : "Add to watchlist";

  if (variant === "icon") {
    return (
      <button
        className={[
          "watchlist-icon",
          isWatched ? "watchlist-icon--on" : "",
          loading ? "watchlist-icon--loading" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleClick}
        disabled={loading}
        data-tour="watch-star"
        aria-label={ariaLabel}
        title={ariaLabel}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 18 18"
          fill={isWatched ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth={isWatched ? 1.2 : 1.3}
          aria-hidden="true"
        >
          <path d={STAR_PATH} />
        </svg>
      </button>
    );
  }

  if (variant === "pill") {
    return (
      <button
        className={[
          "watchlist-pill",
          isWatched ? "watchlist-pill--on" : "",
          loading ? "watchlist-pill--loading" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleClick}
        disabled={loading}
        aria-label={ariaLabel}
        title={ariaLabel}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 18 18"
          fill={isWatched ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth={isWatched ? 1.2 : 1.4}
          aria-hidden="true"
        >
          <path d={STAR_PATH} />
        </svg>
        <span>{isWatched ? "Watching" : "Watch"}</span>
      </button>
    );
  }

  return (
    <button
      className={[
        "watchlist-toggle",
        isWatched ? "watchlist-toggle--on" : "",
        loading ? "watchlist-toggle--loading" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleClick}
      disabled={loading}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {isWatched ? "★" : "☆"}
    </button>
  );
};

export default WatchlistToggle;
